
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ResourceManagerService, ResourceSet } from 'libs/abc/core/resources/src';
import { FlexLayoutModule } from 'ngx-flexible-layout';

import { _AbcButtonComponent } from '../abc-button';
import { _AbcSelectBoxComponent } from '../abc-select-box';

@Component({
    selector: 'abc-camera-details',
    styleUrls: ['abc-camera-details.component.scss'],
    imports: [_AbcSelectBoxComponent, FlexLayoutModule, _AbcButtonComponent],
    template: `
    <div class="camera-details-container">
      <video id="video" playsinline autoplay></video>
      <canvas id="canvas" hidden></canvas>
    
      <div class="controls">
        <abc-button id="snap" (click)="capture()" [text]="res.get('abc-camera-details-take-picture')"> </abc-button>
    
        @if (showCameraSelection) {
          <div>
            <abc-select-box
              [label]="'Device'"
              [dataSource]="cameraDevices"
              [displayExpr]="'displayText'"
              (onValueChanged)="selectCamera()"
              [width]="300"
              [(value)]="selectedCameraDevice"
              >
            </abc-select-box>
          </div>
        }
      </div>
    </div>
    `
})
export class AbcCameraDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() onClose = new EventEmitter();
  private changeDetectorRef: ChangeDetectorRef;
  private videoTag: HTMLMediaElement;
  private stream: MediaStream | null = null;

  public insertImage: (filename: string, dataUrl: string) => void;
  public selectedCameraDevice: CameraDevice;
  public cameraDevices: CameraDevice[] = new Array<CameraDevice>();
  public showCameraSelection: boolean = true;
  public res = ResourceSet.Empty;
  private resAsync: Promise<ResourceSet>;

  constructor(private resourceService: ResourceManagerService) {
    this.resAsync = this.resourceService.getResourceSet('abc-ui-components-abcCameraDetails');
  }

  async ngOnInit() {
    this.videoTag = document.getElementById('video') as HTMLMediaElement;

    navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: true,
      })
      .then((stream) => {
        this.stream = stream;

        navigator.mediaDevices.enumerateDevices().then((devices) => {
          const videoDevices = devices.filter((device) => device.kind === 'videoinput');

          this.showCameraSelection = videoDevices.length > 1;

          videoDevices.forEach((device) => {
            const newCameraDevice: CameraDevice = new CameraDevice();
            newCameraDevice.label = device.label;
            newCameraDevice.deviceId = device.deviceId;

            const existingDevices: CameraDevice[] = this.cameraDevices.filter((d) => d.label === device.label);
            const lastCamIndex = existingDevices.length - 1;
            if (existingDevices.length > 0 && !existingDevices[lastCamIndex].index) {
              existingDevices[lastCamIndex].index = lastCamIndex;
              newCameraDevice.index = existingDevices.length;
            }
            this.cameraDevices.push(newCameraDevice);
          });

          if (videoDevices.length > 1) {
            const lastCamIndex = this.cameraDevices.length - 1;
            this.selectedCameraDevice = this.cameraDevices[lastCamIndex];
          } else {
            this.selectedCameraDevice = this.cameraDevices[0];
          }

          if (!this.showCameraSelection) {
            this.startVideo();
          }

          this.changeDetectorRef?.detectChanges();
        });
      })
      .catch((error) => {
        console.log('Error accessing camera:', error);
      });
  }

  async ngAfterViewInit(): Promise<void> {
    this.res = await this.resAsync;
  }

  ngOnDestroy() {
    this.stopVideo();
  }

  private startVideo(): void {
    navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: {
          deviceId: this.selectedCameraDevice.deviceId,
        },
      })
      .then((stream) => {
        this.stream = stream;
        this.videoTag.srcObject = stream;
        this.videoTag.play();
      });
  }

  private stopVideo(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
      this.videoTag.srcObject = null;
    }
  }

  public selectCamera() {
    this.stopVideo();
    this.startVideo();
  }

  public capture() {
    const videoSource = document.getElementById('video') as any;
    const canvas = document.getElementById('canvas') as HTMLCanvasElement;
    canvas.width = videoSource.videoWidth;
    canvas.height = videoSource.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(videoSource, 0, 0);
    this.insertImage('.jpg', canvas.toDataURL('image/jpeg'));

    this.changeDetectorRef?.detectChanges();

    this.stopVideo();
    this.onClose.emit();
  }
}

class CameraDevice {
  public label: string;
  public deviceId: string;
  public index?: number;
  public get displayText(): string {
    return this.label + (this.index || this.index === 0 ? ' [' + this.index + ']' : '');
  }
}
